<script lang="ts">
	import type { PageData } from './$types';
	import { ShoppingBasket } from 'lucide-svelte';
	import type { Component } from 'svelte';
	import SocialMetaTags from '$lib/components/marketing/SocialMetaTags.svelte';
	import StructuredData from '$lib/components/marketing/StructuredData.svelte';
	import { testimonials } from '$lib/data/testimonials';

	let { data } = $props<{ data: PageData }>();

	// Lazy load the map component only when needed
	let MarketsMapComponent = $state<Component | null>(null);
	let loadingMap = $state(false);
	let showMap = $state(false);

	async function loadMap() {
		if (MarketsMapComponent || loadingMap) return;
		loadingMap = true;
		try {
			const module = await import('$lib/components/maps/MarketsMap.svelte');
			MarketsMapComponent = module.default as Component;
			showMap = true;
		} catch (error) {
			console.error('Failed to load map:', error);
		} finally {
			loadingMap = false;
		}
	}
</script>

<svelte:head>
	<title>LocallyGrown - Farmers Market Platform Built by a Farmer</title>
	<meta
		name="description"
		content="23 years proven, rebuilt for 2025. The farmers market platform built by someone who actually ran one for 20 years. 3% pricing, responsive support, sustainable software."
	/>
</svelte:head>

<SocialMetaTags
	title="LocallyGrown - Farmers Market Platform Built by a Farmer and Market Manager"
	description="23 years proven, rebuilt for 2025. The farmers market platform built by someone who actually ran one for 20 years. 3% pricing, responsive support, sustainable software."
	url="/"
/>

<StructuredData
	type="Organization"
	data={{
		name: 'LocallyGrown',
		url: 'https://locallygrown.net',
		logo: 'https://locallygrown.net/images/2025LGLogo.png',
		description:
			'Farmers market platform built by someone who actually ran one for 20 years. 3% pricing, responsive support, sustainable software.',
		founder: {
			'@type': 'Person',
			name: 'Eric Wagoner',
		},
		contactPoint: {
			'@type': 'ContactPoint',
			email: 'eric@locallygrown.net',
			contactType: 'Customer Service',
		},
	}}
/>

<StructuredData
	type="WebSite"
	data={{
		name: 'LocallyGrown',
		url: 'https://locallygrown.net',
		description:
			'The farmers market platform built by someone who actually ran one for 20 years. 3% pricing, responsive support, sustainable software.',
		potentialAction: {
			'@type': 'SearchAction',
			target: 'https://locallygrown.net/search?q={search_term_string}',
			'query-input': 'required name=search_term_string',
		},
	}}
/>

<!-- Hero Section -->
<section class="hero">
	<div class="container">
		<h1>The Farmers Market Platform Built by Someone Who Actually Ran One</h1>
		<p>
			For 20 years, I ran a 60-grower market while building this software. Not a tech startup. Not a
			movement. Just reliable software with responsive support.
		</p>
		<p>Modern architecture. Sustainable 3% pricing. 23 years of proven results.</p>
		<div class="hero-buttons">
			<a href="/markets/new" class="btn btn-primary">Start Your Market Free</a>
			<a href="/markets" class="btn btn-secondary">See Live Markets</a>
		</div>
	</div>
</section>

<!-- Stats Section -->
<section class="stats-section">
	<div class="container">
		<div class="stats-grid">
			<div class="stat-item">
				<div class="stat-number">$32M+</div>
				<div class="stat-description">Local Food Sales</div>
			</div>
			<div class="stat-item">
				<div class="stat-number">942,000+</div>
				<div class="stat-description">Orders Fulfilled</div>
			</div>
			<div class="stat-item">
				<div class="stat-number">5,400+</div>
				<div class="stat-description">Farmers Supported</div>
			</div>
			<div class="stat-item">
				<div class="stat-number">23 Years</div>
				<div class="stat-description">Proven & Reliable</div>
			</div>
		</div>
		<p class="stats-tagline">
			Built and maintained by one farmer who's been doing this for 23 years
		</p>
	</div>
</section>

<!-- Origin Story Section -->
<section class="origin-story">
	<div class="container">
		<div class="origin-header">
			<h2>Why We're Different</h2>
			<h3>Built from 20 Years of Real Market Experience</h3>
		</div>

		<div class="origin-content">
			<div class="origin-image">
				<img
					loading="lazy"
					src="/images/eric-alg.jpg"
					alt="Eric Wagoner managing Athens Locally Grown farmers market with 60 growers and 1,500 customers"
					width="600"
					height="400"
				/>
			</div>

			<div class="origin-text">
				<p>
					In 2002, I was a farmer struggling with traditional markets. The waste was crushing:
					harvesting speculatively, watching produce spoil or racing around trying to unload it for
					pennies on the dollar, never knowing what would sell.
				</p>

				<p>
					So I built what became the first online farmers market platform. For 20 years, I ran
					Athens Locally Grown (60 growers, 1,500 customers, $15K/week) while developing this
					software.
				</p>

				<p>
					Over 23 years, the platform has processed over $32 million in sales through nearly one
					million orders for 5,400+ farmers.
				</p>

				<p>
					But by 2022, technical debt had caught up with me. The platform was slower, harder to
					maintain. I could have let it die.
				</p>

				<p>
					Instead, I spent 2025 rebuilding everything from scratch. Modern SvelteKit architecture.
					Mobile-first. Fast, stable, secure.
				</p>

				<p class="origin-emphasis">
					<strong>This is still just me—no staff, no investors, no corporate overhead.</strong> That's
					why I respond to your emails personally. That's why pricing stays simple. That's why this is
					a labor of love, not a growth target.
				</p>

				<p class="origin-signature">— Eric Wagoner, Farmer & Developer</p>

				<a href="/story" class="origin-link">Read our 23-year story →</a>
			</div>
		</div>
	</div>
</section>

<!-- Note About Scale Section -->
<section class="scale-note">
	<div class="container">
		<div class="scale-note-card">
			<h3 class="scale-note-title">A Note About Scale</h3>
			<p class="scale-note-intro"><strong>Yes, it's just me running this.</strong></p>

			<div class="scale-note-grid">
				<div class="scale-note-column">
					<h4 class="scale-note-heading scale-note-heading--positive">That means:</h4>
					<ul class="scale-note-list scale-note-list--positive">
						<li>When you email, you get me and not a support queue</li>
						<li>I understand farmer markets because I ran one for 20 years</li>
						<li>There's no corporate bureaucracy slowing things down</li>
						<li>Pricing stays simple because I don't have investor returns to hit</li>
					</ul>
				</div>

				<div class="scale-note-column">
					<h4 class="scale-note-heading scale-note-heading--neutral">But also:</h4>
					<ul class="scale-note-list scale-note-list--neutral">
						<li>I don't have 24/7 support (but I respond within hours during business days)</li>
						<li>Big feature requests take time (I'm one developer)</li>
						<li>If something happens to me, you'll need a transition plan</li>
					</ul>
				</div>
			</div>

			<p class="scale-note-conclusion">
				I'm honest about the trade-offs. Some markets need an enterprise vendor with a big team. But
				many prefer working directly with someone who's lived this life.
			</p>
		</div>
	</div>
</section>

<!-- Features Section -->
<section class="features">
	<div class="container">
		<h2 class="mb-4 text-center">Everything You Need, Nothing You Don't</h2>
		<div class="features-grid">
			<div class="feature-card">
				<div class="feature-icon">🌱</div>
				<h3>Simple for Farmers</h3>
				<p>
					Add products with your phone. See what's ordered. Get paid automatically. That's it—back
					to farming.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">
					<ShoppingBasket size={48} />
				</div>
				<h3>Convenient for Customers</h3>
				<p>
					Shop Tuesday night. Pick up Saturday morning. Know exactly what's available before you
					drive to the market.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">📊</div>
				<h3>Organized for Managers</h3>
				<p>
					See all orders in one place. Print labels. Track payments. Run pickup day like clockwork.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">💳</div>
				<h3>Flexible Payments</h3>
				<p>
					Accept cards online or cash at pickup. Farmers get paid directly—we never hold your money.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">📱</div>
				<h3>Works on Any Device</h3>
				<p>Phone, tablet, or computer—it just works. No app to download, nothing to install.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">🔧</div>
				<h3>Your Market, Your Brand</h3>
				<p>Add your logo and colors. It'll look like your market, not some generic website.</p>
			</div>
			<div class="feature-card showcase-feature">
				<div class="feature-icon">📱</div>
				<h3>Built with Mobile-First Design</h3>
				<p>
					Every interaction is crafted for touch, optimized for speed, and accessible to all.
					Explore our comprehensive component library and design system that powers beautiful market
					experiences.
				</p>
				<a href="/showcase" class="feature-link">View Design Showcase →</a>
			</div>
		</div>
		<p class="subtle-link">
			Want to see what's coming next? <a href="/roadmap">Check out our product roadmap →</a>
		</p>
	</div>
</section>

<!-- Map Section -->
<section class="markets-section">
	<div class="container">
		<h2 class="mb-4 text-center">Find a Market Near You</h2>
		{#if data.mapMarkers}
			<div class="map-container">
				{#if showMap && MarketsMapComponent}
					{@const MapComponent = MarketsMapComponent}
					<MapComponent markers={data.mapMarkers} />
				{:else}
					<div
						class="map-placeholder"
						onclick={loadMap}
						onkeydown={(e) => e.key === 'Enter' && loadMap()}
						role="button"
						tabindex="0"
					>
						<div class="placeholder-content">
							<div class="placeholder-icon">🗺️</div>
							<h3>Interactive Market Map</h3>
							<p>Click to load an interactive map showing all markets</p>
							{#if loadingMap}
								<div class="loading-spinner"></div>
							{:else}
								<button class="load-map-btn" type="button">Load Map</button>
							{/if}
						</div>
					</div>
				{/if}
			</div>
		{/if}
		<div class="mt-4 text-center">
			<a href="/markets" class="btn btn-primary">Browse All Markets</a>
		</div>
	</div>
</section>

<!-- How It Works -->
<section class="features">
	<div class="container">
		<h2 class="mb-4 text-center">How It Works</h2>
		<div class="features-grid">
			<div class="feature-card">
				<div class="feature-icon">1</div>
				<h3>Farmers List Products</h3>
				<p>Monday morning: snap a photo, set a price, done. Takes about 30 seconds per product.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">2</div>
				<h3>Customers Order Online</h3>
				<p>
					Order window opens Tuesday. Customers browse, add to cart, check out. Simple as any online
					store.
				</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">3</div>
				<h3>Farmers Harvest to Order</h3>
				<p>Friday: print your pick list. Harvest exactly what's sold. No waste, no guessing.</p>
			</div>
			<div class="feature-card">
				<div class="feature-icon">4</div>
				<h3>Easy Pickup Day</h3>
				<p>
					Saturday: everything's labeled and sorted. Customers grab their orders, chat a bit, head
					home happy.
				</p>
			</div>
		</div>
	</div>
</section>

<!-- Pricing Section -->
<section class="pricing-preview">
	<div class="container">
		<div class="pricing-header">
			<h2>Simple, Sustainable Pricing</h2>
			<h3>3% of Sales. That's It.</h3>
		</div>

		<!-- New Market Bonus -->
		<div class="new-market-bonus">
			<h4>🎁 New Market Bonus</h4>
			<p class="bonus-headline">
				<strong>Your first $15,000 in sales is FREE.</strong>
			</p>
			<p class="bonus-detail">
				That's a $450 credit—enough for many markets to run their entire first season at no cost. I
				want you to succeed before I get paid.
			</p>
		</div>

		<div class="pricing-grid">
			<div class="pricing-included">
				<h4>What's Included</h4>
				<ul>
					<li><span class="check-icon">✓</span> No monthly minimums</li>
					<li><span class="check-icon">✓</span> No setup fees</li>
					<li><span class="check-icon">✓</span> No contracts</li>
					<li><span class="check-icon">✓</span> Seasonal pause (pay $0 off-season)</li>
					<li><span class="check-icon">✓</span> Hosting, support, all features</li>
					<li><span class="check-icon">✓</span> Growers never pay us directly</li>
				</ul>
			</div>

			<div class="pricing-comparison">
				<h4>How It Compares</h4>
				<div class="table-wrapper">
					<table>
						<thead>
							<tr>
								<th>Monthly Sales</th>
								<th>You Pay</th>
								<th>Others Charge</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td data-label="Monthly Sales">$1,000</td>
								<td data-label="You Pay" class="highlight">$30</td>
								<td data-label="Others Charge">$49-$149</td>
							</tr>
							<tr>
								<td data-label="Monthly Sales">$5,000</td>
								<td data-label="You Pay" class="highlight">$150</td>
								<td data-label="Others Charge">$149-$299</td>
							</tr>
							<tr>
								<td data-label="Monthly Sales">$10,000</td>
								<td data-label="You Pay" class="highlight">$300</td>
								<td data-label="Others Charge">$299-$449</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<p class="pricing-subtext">
			Commission pricing means you only pay when you succeed. Perfect for seasonal markets and
			emerging food systems.
		</p>

		<p class="subtle-link">
			Wondering how we compare to alternatives?
			<a href="/compare">See an honest comparison →</a>
		</p>
	</div>
</section>

<!-- Testimonials -->
<section class="testimonials">
	<div class="container">
		<div class="testimonials-header">
			<h2>Markets That Trust Us</h2>
			<p class="testimonials-subtitle">Real feedback from market managers across the country</p>
		</div>
		<div class="testimonials-grid">
			{#each testimonials as testimonial (testimonial.author)}
				<div class="testimonial-card">
					<div class="quote-icon">"</div>
					<p class="testimonial-text">
						{testimonial.quote}
					</p>
					<div class="testimonial-footer">
						<div class="testimonial-author">{testimonial.author}</div>
						<div class="testimonial-role">
							{testimonial.role},
							<a
								href="https://{testimonial.subdomain}.locallygrown.net"
								target="_blank"
								rel="noopener noreferrer"
								class="market-link"
							>
								{testimonial.marketName}
							</a>
						</div>
					</div>
				</div>
			{/each}
		</div>
	</div>
</section>

<!-- About Solo Section -->
<section class="about-solo">
	<div class="container">
		<div class="about-solo-header">
			<h2>Working with a Solo Founder</h2>
		</div>

		<div class="about-solo-content">
			<p class="about-solo-intro">
				I'm Eric. I built this platform in 2002 while farming, ran Athens Locally Grown for 20
				years, and recently rebuilt everything with modern technology.
			</p>

			<p class="about-solo-emphasis">
				<strong>It's just me.</strong> No staff, no investors, no corporate structure.
			</p>

			<h3 class="about-solo-subheading">What that means for you:</h3>

			<div class="about-solo-grid">
				<div class="about-solo-point">
					<h4>Direct Support</h4>
					<p>
						When you email me, you get me—not a support queue. I typically respond within a few
						hours during business days (Eastern time). I understand the problems you're facing
						because I lived them for two decades.
					</p>
				</div>

				<div class="about-solo-point">
					<h4>Practical Development</h4>
					<p>
						Feature development is slower than a VC-backed company with a team of developers. But
						it's faster than open-source consensus-building. I ship what markets actually need, not
						what looks good in a pitch deck.
					</p>
				</div>

				<div class="about-solo-point">
					<h4>Proven Reliability</h4>
					<p>
						The platform has processed $32 million in sales over 23 years. It's proven, stable, and
						reliable. But yes, it depends on me staying healthy and committed. I'm 54, plan to do
						this for another 20 years, and have succession planning in place just in case.
					</p>
				</div>
			</div>

			<p class="about-solo-conclusion">
				If you want a corporate vendor with enterprise SLAs, I'm not your solution. If you want to
				work with someone who genuinely cares about your market's success because I lived this life,
				let's talk.
			</p>
		</div>
	</div>
</section>

<!-- CTA Section -->
<section class="cta-section">
	<div class="container">
		<h2>Ready to Get Started?</h2>
		<p>Free to try. No credit card needed. Get your market online in about 20 minutes.</p>
		<a href="/markets/new" class="btn btn-primary">Create Your Market Today</a>
	</div>
</section>

<style>
	/* ========================================
	   Layout Utilities
	   ======================================== */
	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1.5rem;
	}

	.text-center {
		text-align: center;
	}

	.mb-4 {
		margin-bottom: 2rem;
	}

	.mt-4 {
		margin-top: 2rem;
	}

	/* ========================================
	   Hero Section
	   ======================================== */
	.hero {
		padding: 5rem 0 6rem;
		background: var(--marketing-hero-bg-gradient);
		text-align: center;
		position: relative;
		overflow: hidden;
	}

	/* Subtle decorative gradient overlay */
	.hero::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: var(--marketing-hero-bg-overlay);
		pointer-events: none;
	}

	.hero .container {
		position: relative;
		z-index: 1;
	}

	.hero h1 {
		font-size: 3rem;
		margin-bottom: 1.5rem;
		color: var(--marketing-hero-heading);
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.02em;
	}

	.hero p {
		font-size: 1.25rem;
		max-width: 700px;
		margin: 0 auto 1rem;
		color: var(--marketing-text-primary);
		line-height: 1.65;
		font-weight: 400;
	}

	/* Make the first paragraph stand out more */
	.hero p:first-of-type {
		font-size: 1.35rem;
		font-weight: 500;
		color: var(--marketing-hero-text-primary);
		margin-bottom: 1.25rem;
	}

	/* Second paragraph should be slightly smaller but still readable */
	.hero p:nth-of-type(2) {
		font-size: 1.15rem;
		color: var(--marketing-hero-text-secondary);
		font-weight: 600;
	}

	/* Dark mode hero text colors - optimized for readability */

	.hero-buttons {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
		margin-top: 2.5rem;
	}

	.hero-buttons .btn {
		padding: 1rem 2rem;
		border-radius: 8px;
		text-decoration: none;
		font-weight: 600;
		transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
		font-size: 1.125rem;
		box-shadow: var(--marketing-shadow-sm);
		position: relative;
		overflow: hidden;
	}

	/* Primary button with gradient hover effect */
	.hero-buttons .btn-primary {
		background: var(--marketing-gradient-primary);
		color: var(--marketing-text-white);
		border: 2px solid transparent;
		z-index: 1;
	}

	.hero-buttons .btn-primary::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(
			135deg,
			var(--marketing-primary-hover) 0%,
			var(--marketing-primary) 100%
		);
		opacity: 0;
		transition: opacity 0.25s ease;
		z-index: -1;
	}

	.hero-buttons .btn-primary:hover::before,
	.hero-buttons .btn-primary:focus::before {
		opacity: 1;
	}

	.hero-buttons .btn-primary:hover,
	.hero-buttons .btn-primary:focus,
	.hero-buttons .btn-primary:active {
		transform: translateY(-3px);
		box-shadow: var(--marketing-shadow-lg);
	}

	/* Secondary button with smooth transition */
	.hero-buttons .btn-secondary {
		background: var(--marketing-bg-white);
		color: var(--marketing-primary);
		border: 2px solid var(--marketing-primary);
		backdrop-filter: blur(10px);
	}

	.hero-buttons .btn-secondary:hover,
	.hero-buttons .btn-secondary:focus {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		transform: translateY(-3px);
		box-shadow: var(--marketing-shadow-lg);
		border-color: var(--marketing-primary);
	}

	.hero-buttons .btn-secondary:active {
		transform: translateY(-1px);
	}

	/* ========================================
	   Stats Section
	   ======================================== */
	.stats-section {
		padding: 3rem 0;
		background: var(--marketing-bg-secondary);
	}

	.stats-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
		gap: 2rem;
		text-align: center;
	}

	.stat-item {
		padding: 1.5rem;
	}

	.stat-number {
		font-size: 3rem;
		font-weight: 700;
		color: var(--marketing-primary);
		margin-bottom: 0.5rem;
		line-height: 1;
	}

	.stat-description {
		font-size: 1.1rem;
		color: var(--marketing-text-secondary);
		font-weight: 500;
	}

	.stats-tagline {
		text-align: center;
		color: var(--marketing-text-secondary);
		font-size: 1.0625rem;
		margin-top: 2rem;
		padding-top: 2rem;
		border-top: 2px solid var(--marketing-border-light);
		max-width: 600px;
		margin-left: auto;
		margin-right: auto;
		font-style: italic;
		line-height: 1.6;
	}

	/* ========================================
	   Origin Story Section
	   ======================================== */
	.origin-story {
		padding: 4rem 0;
		background: var(--marketing-bg-white);
	}

	.origin-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.origin-header h2 {
		font-size: 2.5rem;
		margin-bottom: 0.75rem;
		color: var(--marketing-primary);
	}

	.origin-header h3 {
		font-size: 1.5rem;
		color: var(--marketing-text-secondary);
		font-weight: 400;
	}

	.origin-content {
		display: grid;
		grid-template-columns: 1fr;
		gap: 3rem;
		align-items: start;
	}

	.origin-image {
		width: 100%;
		max-width: 500px;
		margin: 0 auto;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: var(--marketing-shadow-lg);
	}

	.origin-image img {
		width: 100%;
		height: auto;
		display: block;
		object-fit: cover;
	}

	.origin-text {
		max-width: 700px;
		margin: 0 auto;
	}

	.origin-text p {
		font-size: 1.125rem;
		line-height: 1.7;
		margin-bottom: 1.5rem;
		color: var(--marketing-text-muted);
	}

	.origin-emphasis {
		font-weight: 600;
		color: var(--marketing-text-primary) !important;
		font-size: 1.25rem !important;
		padding: 1.5rem;
		background: var(--marketing-bg-secondary);
		border-left: 4px solid var(--marketing-primary);
		border-radius: 0 8px 8px 0;
		margin: 2rem 0 !important;
	}

	.origin-signature {
		font-style: italic;
		color: var(--marketing-text-secondary) !important;
		font-size: 1.125rem !important;
		margin-top: 2rem !important;
	}

	.origin-link {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		margin-top: 2rem;
		color: var(--marketing-primary);
		text-decoration: none;
		font-weight: 600;
		font-size: 1.125rem;
		transition: all 0.3s ease;
	}

	.origin-link:hover,
	.origin-link:focus {
		color: var(--marketing-primary-hover);
		transform: translateX(4px);
	}

	@media (min-width: 768px) {
		.origin-content {
			grid-template-columns: 1fr 1.2fr;
			gap: 4rem;
		}

		.origin-image {
			margin: 0;
		}

		.origin-text {
			margin: 0;
		}
	}

	/* ========================================
	   Features Section
	   ======================================== */
	.features {
		padding: 4rem 0;
		background: var(--marketing-bg-white);
	}

	.features h2 {
		font-size: 2.5rem;
		color: var(--marketing-primary);
	}

	.features-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 2rem;
		margin-top: 3rem;
	}

	.feature-card {
		background: var(--marketing-bg-white);
		border: 2px solid var(--marketing-border-light);
		border-radius: 12px;
		padding: 2rem;
		text-align: center;
		transition: all 0.3s ease;
		box-shadow: var(--marketing-shadow-sm);
	}

	.feature-card:hover,
	.feature-card:focus,
	.feature-card:active {
		transform: translateY(-4px);
		box-shadow: var(--marketing-shadow-lg);
		border-color: var(--marketing-primary);
	}

	.feature-icon {
		font-size: 2.5rem;
		margin-bottom: 1rem;
		display: flex;
		align-items: center;
		justify-content: center;
		height: 60px;
		color: var(--marketing-accent);
	}

	.feature-card h3 {
		color: var(--marketing-primary);
		font-size: 1.25rem;
		margin-bottom: 0.75rem;
	}

	.feature-card p {
		color: var(--marketing-text-muted);
		font-size: 1rem;
		line-height: 1.6;
		margin-bottom: 0;
	}

	/* Special styling for the How It Works section */
	.features .feature-card .feature-icon {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		border-radius: 50%;
		width: 60px;
		height: 60px;
		font-size: 1.75rem;
		font-weight: 700;
		margin: 0 auto 1.5rem;
	}

	/* Showcase Feature Card */
	.showcase-feature {
		background: var(--marketing-bg-secondary);
		border: 2px solid var(--marketing-border-light);
		position: relative;
		overflow: hidden;
	}

	.showcase-feature::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: var(--marketing-gradient-primary);
	}

	.feature-link {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		margin-top: 1rem;
		color: var(--marketing-primary);
		text-decoration: none;
		font-weight: 600;
		font-size: 0.95rem;
		transition: all 0.3s ease;
	}

	.feature-link:hover,
	.feature-link:focus {
		color: var(--marketing-primary-hover);
		transform: translateX(4px);
	}

	/* ========================================
	   Map Section
	   ======================================== */
	.markets-section {
		padding: 4rem 0;
		background: var(--marketing-bg-white);
	}

	.markets-section h2 {
		font-size: 2.5rem;
		color: var(--marketing-primary);
	}

	.map-container {
		height: 400px;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: var(--marketing-shadow-md);
		margin: 2rem 0;
	}

	.map-placeholder {
		height: 400px;
		border-radius: 12px;
		background: linear-gradient(
			135deg,
			var(--marketing-bg-tertiary) 0%,
			var(--marketing-bg-secondary) 100%
		);
		border: 2px dashed var(--marketing-border-light);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s ease;
		margin: 2rem 0;
	}

	.map-placeholder:hover,
	.map-placeholder:focus {
		border-color: var(--marketing-primary);
		background: linear-gradient(
			135deg,
			var(--marketing-bg-secondary) 0%,
			var(--marketing-bg-tertiary) 100%
		);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	.placeholder-content {
		text-align: center;
		padding: 2rem;
	}

	.placeholder-icon {
		font-size: 3rem;
		margin-bottom: 1rem;
	}

	.placeholder-content h3 {
		color: var(--marketing-primary);
		margin-bottom: 0.5rem;
		font-size: 1.5rem;
	}

	.placeholder-content p {
		color: var(--marketing-text-muted);
		margin-bottom: 1.5rem;
	}

	.load-map-btn {
		background: var(--marketing-primary);
		color: var(--marketing-text-white);
		border: none;
		padding: 0.75rem 1.5rem;
		border-radius: 6px;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.load-map-btn:hover,
	.load-map-btn:focus {
		background: var(--marketing-primary-hover);
		transform: translateY(-1px);
		box-shadow: var(--marketing-shadow-sm);
	}

	.loading-spinner {
		width: 32px;
		height: 32px;
		border: 3px solid var(--marketing-border-light);
		border-top: 3px solid var(--marketing-primary);
		border-radius: 50%;
		animation: spin 1s linear infinite;
		margin: 0 auto;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	/* ========================================
	   Pricing Section
	   ======================================== */
	.pricing-preview {
		padding: 4rem 0;
		background: var(--marketing-bg-secondary);
	}

	.pricing-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.pricing-header h2 {
		font-size: 2.5rem;
		margin-bottom: 0.75rem;
		color: var(--marketing-primary);
	}

	.pricing-header h3 {
		font-size: 1.75rem;
		color: var(--marketing-accent);
		font-weight: 600;
	}

	.new-market-bonus {
		background: var(--marketing-success-bg);
		border: 2px solid var(--marketing-success);
		border-radius: 12px;
		padding: 2rem;
		margin-bottom: 3rem;
		box-shadow: var(--marketing-shadow-md);
		text-align: center;
		max-width: 800px;
		margin-left: auto;
		margin-right: auto;
	}

	.new-market-bonus h4 {
		color: var(--marketing-success);
		font-size: 1.5rem;
		margin: 0 0 1rem 0;
		font-weight: 700;
	}

	.new-market-bonus .bonus-headline {
		font-size: 1.3rem;
		margin-bottom: 0.75rem;
		color: var(--marketing-text-primary);
	}

	.new-market-bonus .bonus-headline strong {
		color: var(--marketing-success);
		font-weight: 700;
	}

	.new-market-bonus .bonus-detail {
		color: var(--marketing-text-secondary);
		margin-bottom: 0;
		font-size: 1.05rem;
		line-height: 1.6;
	}

	.pricing-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 2rem;
		margin-bottom: 2rem;
	}

	.pricing-included,
	.pricing-comparison {
		background: var(--marketing-bg-white);
		border: 2px solid var(--marketing-border-light);
		border-radius: 12px;
		padding: 2rem;
		box-shadow: var(--marketing-shadow-sm);
	}

	.pricing-included h4,
	.pricing-comparison h4 {
		font-size: 1.5rem;
		color: var(--marketing-primary);
		margin-bottom: 1.5rem;
		text-align: center;
	}

	.pricing-included ul {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.pricing-included li {
		padding: 0.75rem 0;
		border-bottom: 1px solid var(--marketing-border-light);
		font-size: 1.125rem;
		color: var(--marketing-text-muted);
		display: flex;
		align-items: center;
		gap: 0.75rem;
	}

	.pricing-included li:last-child {
		border-bottom: none;
	}

	.check-icon {
		color: var(--marketing-success);
		font-weight: 700;
		font-size: 1.25rem;
		flex-shrink: 0;
	}

	.table-wrapper {
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	.pricing-comparison table {
		width: 100%;
		border-collapse: collapse;
		margin: 0;
	}

	.pricing-comparison th,
	.pricing-comparison td {
		padding: 1rem;
		text-align: left;
		border-bottom: 1px solid var(--marketing-border-light);
	}

	.pricing-comparison th {
		font-weight: 600;
		color: var(--marketing-text-primary);
		background: var(--marketing-bg-tertiary);
		font-size: 1.125rem;
	}

	.pricing-comparison td {
		color: var(--marketing-text-muted);
		font-size: 1.125rem;
	}

	.pricing-comparison td.highlight {
		color: var(--marketing-primary);
		font-weight: 700;
	}

	.pricing-comparison tr:last-child td {
		border-bottom: none;
	}

	.pricing-subtext {
		text-align: center;
		font-size: 1.125rem;
		color: var(--marketing-text-muted);
		max-width: 700px;
		margin: 2rem auto 0;
		line-height: 1.6;
	}

	.subtle-link {
		text-align: center;
		font-size: 1rem;
		color: var(--marketing-text-secondary);
		max-width: 600px;
		margin: 1.5rem auto 0;
		font-style: italic;
	}

	.subtle-link a {
		color: var(--marketing-primary);
		text-decoration: none;
		font-weight: 500;
		border-bottom: 1px solid transparent;
		transition: all 0.2s ease;
		font-style: normal;
	}

	.subtle-link a:hover,
	.subtle-link a:focus,
	.subtle-link a:active {
		color: var(--marketing-primary-hover);
		border-bottom-color: var(--marketing-primary-hover);
	}

	@media (min-width: 768px) {
		.pricing-grid {
			grid-template-columns: 1fr 1.2fr;
			gap: 3rem;
		}

		.pricing-comparison th,
		.pricing-comparison td {
			text-align: center;
		}

		.pricing-comparison th:first-child,
		.pricing-comparison td:first-child {
			text-align: left;
		}
	}

	/* ========================================
	   Testimonials Section
	   ======================================== */
	.testimonials {
		padding: 4rem 0;
		background: var(--marketing-bg-white);
	}

	.testimonials-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.testimonials-header h2 {
		font-size: 2.5rem;
		margin-bottom: 0.75rem;
		color: var(--marketing-primary);
	}

	.testimonials-subtitle {
		font-size: 1.125rem;
		color: var(--marketing-text-secondary);
		font-weight: 400;
		margin: 0;
	}

	.testimonials-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 2rem;
		margin-top: 3rem;
	}

	.testimonial-card {
		background: var(--marketing-bg-white);
		border: 2px solid var(--marketing-border-light);
		border-radius: 12px;
		padding: 2rem;
		transition: all 0.3s ease;
		box-shadow: var(--marketing-shadow-sm);
		display: flex;
		flex-direction: column;
		position: relative;
		overflow: hidden;
	}

	.testimonial-card::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		height: 4px;
		background: var(--marketing-gradient-primary);
		opacity: 0;
		transition: opacity 0.3s ease;
	}

	.testimonial-card:hover,
	.testimonial-card:focus,
	.testimonial-card:active {
		transform: translateY(-4px);
		box-shadow: var(--marketing-shadow-lg);
		border-color: var(--marketing-primary);
	}

	.testimonial-card:hover::before,
	.testimonial-card:focus::before,
	.testimonial-card:active::before {
		opacity: 1;
	}

	.quote-icon {
		font-size: 4rem;
		line-height: 1;
		color: var(--marketing-accent);
		opacity: 0.2;
		font-family: Georgia, serif;
		margin-bottom: 0.5rem;
		font-weight: 700;
	}

	.testimonial-text {
		font-size: 1.0625rem;
		line-height: 1.7;
		color: var(--marketing-text-muted);
		margin-bottom: 1.5rem;
		flex-grow: 1;
		font-style: italic;
	}

	.testimonial-footer {
		border-top: 2px solid var(--marketing-bg-secondary);
		padding-top: 1rem;
		margin-top: auto;
	}

	.testimonial-author {
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--marketing-primary);
		margin-bottom: 0.25rem;
	}

	.testimonial-role {
		font-size: 0.9375rem;
		color: var(--marketing-text-secondary);
		font-weight: 400;
	}

	.market-link {
		color: var(--marketing-primary);
		text-decoration: none;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.market-link:hover,
	.market-link:focus,
	.market-link:active {
		color: var(--marketing-primary-dark);
		text-decoration: underline;
	}

	/* ========================================
	   Scale Note Section
	   ======================================== */
	.scale-note {
		padding: 4rem 0;
		background: var(--marketing-bg-secondary);
	}

	.scale-note-card {
		background: var(--marketing-bg-white);
		border: 2px solid var(--marketing-border-light);
		border-left: 4px solid var(--marketing-primary);
		border-radius: 12px;
		padding: 2.5rem;
		box-shadow: var(--marketing-shadow-md);
		max-width: 900px;
		margin: 0 auto;
	}

	.scale-note-title {
		font-size: 2rem;
		color: var(--marketing-primary);
		margin-bottom: 1rem;
		text-align: center;
	}

	.scale-note-intro {
		font-size: 1.25rem;
		text-align: center;
		color: var(--marketing-text-primary);
		margin-bottom: 2rem;
		padding-bottom: 1.5rem;
		border-bottom: 2px solid var(--marketing-bg-secondary);
	}

	.scale-note-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 2rem;
		margin-bottom: 2rem;
	}

	.scale-note-column {
		padding: 1.5rem;
		background: var(--marketing-bg-tertiary);
		border-radius: 8px;
	}

	.scale-note-heading {
		font-size: 1.25rem;
		margin-bottom: 1rem;
		font-weight: 600;
	}

	.scale-note-heading--positive {
		color: var(--marketing-success);
	}

	.scale-note-heading--neutral {
		color: var(--marketing-text-primary);
	}

	.scale-note-list {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.scale-note-list li {
		padding: 0.75rem 0;
		padding-left: 2rem;
		position: relative;
		color: var(--marketing-text-muted);
		line-height: 1.6;
		font-size: 1.0625rem;
	}

	.scale-note-list li:not(:last-child) {
		border-bottom: 1px solid var(--marketing-border-light);
	}

	.scale-note-list--positive li::before {
		content: '✓';
		position: absolute;
		left: 0;
		color: var(--marketing-success);
		font-weight: 700;
		font-size: 1.25rem;
	}

	.scale-note-list--neutral li::before {
		content: '•';
		position: absolute;
		left: 0.25rem;
		color: var(--marketing-text-secondary);
		font-weight: 700;
		font-size: 1.5rem;
		line-height: 1;
	}

	.scale-note-conclusion {
		text-align: center;
		font-size: 1.125rem;
		color: var(--marketing-text-muted);
		line-height: 1.7;
		padding-top: 1.5rem;
		border-top: 2px solid var(--marketing-bg-secondary);
		font-style: italic;
	}

	@media (min-width: 768px) {
		.scale-note-grid {
			grid-template-columns: 1fr 1fr;
		}
	}

	@media (max-width: 768px) {
		.scale-note-card {
			padding: 1.5rem;
		}

		.scale-note-title {
			font-size: 1.5rem;
		}

		.scale-note-intro {
			font-size: 1.125rem;
		}

		.scale-note-list li {
			font-size: 1rem;
		}

		.scale-note-conclusion {
			font-size: 1rem;
		}
	}

	/* ========================================
	   About Solo Section
	   ======================================== */
	.about-solo {
		padding: 4rem 0;
		background: var(--marketing-bg-white);
	}

	.about-solo-header {
		text-align: center;
		margin-bottom: 3rem;
	}

	.about-solo-header h2 {
		font-size: 2.5rem;
		color: var(--marketing-primary);
		margin-bottom: 0;
	}

	.about-solo-content {
		max-width: 900px;
		margin: 0 auto;
	}

	.about-solo-intro {
		font-size: 1.25rem;
		line-height: 1.7;
		color: var(--marketing-text-muted);
		margin-bottom: 1.5rem;
		text-align: center;
	}

	.about-solo-emphasis {
		font-size: 1.5rem;
		font-weight: 600;
		color: var(--marketing-text-primary);
		text-align: center;
		margin: 2rem 0;
		padding: 1.5rem;
		background: var(--marketing-bg-secondary);
		border-left: 4px solid var(--marketing-primary);
		border-radius: 0 8px 8px 0;
	}

	.about-solo-subheading {
		font-size: 1.5rem;
		color: var(--marketing-primary);
		text-align: center;
		margin: 2.5rem 0 2rem;
		font-weight: 600;
	}

	.about-solo-grid {
		display: grid;
		grid-template-columns: 1fr;
		gap: 2rem;
		margin: 2rem 0;
	}

	.about-solo-point {
		background: var(--marketing-bg-secondary);
		border: 2px solid var(--marketing-border-light);
		border-radius: 12px;
		padding: 2rem;
		transition: all 0.3s ease;
	}

	.about-solo-point:hover,
	.about-solo-point:focus,
	.about-solo-point:active {
		transform: translateY(-4px);
		box-shadow: var(--marketing-shadow-lg);
		border-color: var(--marketing-primary);
	}

	.about-solo-point h4 {
		font-size: 1.25rem;
		color: var(--marketing-primary);
		margin-bottom: 0.75rem;
		font-weight: 600;
	}

	.about-solo-point p {
		font-size: 1.0625rem;
		line-height: 1.7;
		color: var(--marketing-text-muted);
		margin: 0;
	}

	.about-solo-conclusion {
		font-size: 1.25rem;
		line-height: 1.7;
		color: var(--marketing-text-primary);
		text-align: center;
		margin-top: 2.5rem;
		padding: 2rem;
		background: var(--marketing-bg-tertiary);
		border-radius: 12px;
		font-weight: 500;
	}

	@media (min-width: 768px) {
		.about-solo-grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	@media (max-width: 768px) {
		.about-solo-header h2 {
			font-size: 2rem;
		}

		.about-solo-intro {
			font-size: 1.125rem;
		}

		.about-solo-emphasis {
			font-size: 1.25rem;
			padding: 1rem;
		}

		.about-solo-subheading {
			font-size: 1.25rem;
		}

		.about-solo-point {
			padding: 1.5rem;
		}

		.about-solo-point h4 {
			font-size: 1.125rem;
		}

		.about-solo-point p {
			font-size: 1rem;
		}

		.about-solo-conclusion {
			font-size: 1.125rem;
			padding: 1.5rem;
		}
	}

	/* ========================================
	   CTA Section
	   ======================================== */
	.cta-section {
		padding: 4rem 0;
		background: var(--marketing-gradient-primary);
		color: var(--marketing-text-white);
		text-align: center;
	}

	.cta-section h2 {
		color: var(--marketing-text-white) !important;
		margin-bottom: 1rem;
		font-size: 2.5rem;
	}

	.cta-section p {
		color: var(--marketing-text-white) !important;
		opacity: 0.95;
		max-width: 600px;
		margin: 0 auto 2rem;
		font-size: 1.125rem;
	}

	.cta-section .btn {
		background: var(--marketing-bg-white);
		color: var(--marketing-primary) !important;
		padding: 0.75rem 2rem;
		font-weight: 600;
		text-decoration: none;
		display: inline-block;
		border-radius: 6px;
		transition: all 0.2s ease;
	}

	.cta-section .btn:hover,
	.cta-section .btn:focus {
		background: var(--marketing-bg-tertiary);
		transform: translateY(-2px);
		box-shadow: var(--marketing-shadow-md);
	}

	/* ========================================
	   Accessibility & Motion
	   ======================================== */

	/* Respect user's motion preferences */
	@media (prefers-reduced-motion: reduce) {
		.hero-buttons .btn,
		.hero-buttons .btn::before,
		.feature-card,
		.testimonial-card {
			transition: none;
		}

		.hero-buttons .btn:hover,
		.hero-buttons .btn:focus,
		.feature-card:hover,
		.testimonial-card:hover {
			transform: none;
		}
	}

	/* Focus states for keyboard navigation */
	.hero-buttons .btn:focus-visible {
		outline: 3px solid var(--marketing-accent);
		outline-offset: 3px;
	}

	/* ========================================
	   Responsive Design
	   ======================================== */

	/* Tablet adjustments */
	@media (max-width: 1024px) {
		.hero h1 {
			font-size: 2.5rem;
		}

		.hero p:first-of-type {
			font-size: 1.3rem;
		}

		.hero p:nth-of-type(2) {
			font-size: 1.125rem;
		}
	}

	/* Mobile styles */
	@media (max-width: 768px) {
		.container {
			padding: 0 1rem;
		}

		.hero {
			padding: 3rem 0 4rem;
		}

		.hero h1 {
			font-size: 2rem;
			line-height: 1.2;
			margin-bottom: 1.25rem;
			letter-spacing: -0.01em;
		}

		.hero p {
			font-size: 1.0625rem;
			padding: 0;
			line-height: 1.6;
		}

		.hero p:first-of-type {
			font-size: 1.125rem;
			margin-bottom: 1rem;
		}

		.hero p:nth-of-type(2) {
			font-size: 1rem;
		}

		.hero-buttons {
			flex-direction: column;
			align-items: stretch;
			margin-top: 2rem;
			gap: 0.875rem;
		}

		.hero-buttons .btn {
			width: 100%;
			text-align: center;
			padding: 0.875rem 1.5rem;
			font-size: 1.0625rem;
		}

		.stats-grid {
			grid-template-columns: 1fr;
			gap: 1.5rem;
		}

		.stat-number {
			font-size: 2.5rem;
		}

		.stats-tagline {
			font-size: 1rem;
			padding-top: 1.5rem;
			margin-top: 1.5rem;
		}

		.origin-header h2,
		.pricing-header h2,
		.features h2,
		.markets-section h2,
		.testimonials-header h2,
		.cta-section h2 {
			font-size: 2rem;
		}

		.origin-header h3,
		.pricing-header h3 {
			font-size: 1.25rem;
		}

		.testimonials-subtitle {
			font-size: 1rem;
		}

		.origin-text p,
		.pricing-subtext {
			font-size: 1rem;
		}

		.origin-emphasis {
			font-size: 1.125rem !important;
			padding: 1rem;
		}

		.features-grid,
		.testimonials-grid {
			grid-template-columns: 1fr;
			gap: 1.5rem;
		}

		.testimonial-text {
			font-size: 1rem;
		}

		.quote-icon {
			font-size: 3rem;
		}

		.map-container,
		.map-placeholder {
			height: 300px;
			border-radius: 8px;
		}

		.placeholder-content {
			padding: 1rem;
		}

		.placeholder-content h3 {
			font-size: 1.25rem;
		}

		.pricing-included li,
		.pricing-comparison td {
			font-size: 1rem;
		}

		/* Responsive table for mobile */
		.pricing-comparison table {
			font-size: 0.9rem;
		}

		.pricing-comparison th,
		.pricing-comparison td {
			padding: 0.75rem 0.5rem;
		}
	}

	@media (max-width: 480px) {
		/* Stack table rows vertically on very small screens */
		.pricing-comparison thead {
			display: none;
		}

		.pricing-comparison tr {
			display: block;
			margin-bottom: 1rem;
			border: 1px solid var(--marketing-border-light);
			border-radius: 8px;
			padding: 0.5rem;
		}

		.pricing-comparison td {
			display: flex;
			justify-content: space-between;
			padding: 0.5rem;
			border-bottom: 1px solid var(--marketing-border-light);
		}

		.pricing-comparison td:last-child {
			border-bottom: none;
		}

		.pricing-comparison td::before {
			content: attr(data-label);
			font-weight: 600;
			color: var(--marketing-text-primary);
		}
	}
</style>
